import { Box, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { Colors } from "Components/Layout/Themes/Colors";
import { ExportActions } from "Components/Transactions/ExportActions";
import type { TransactionFilters } from "Components/Transactions/hooks/useTransactionFilterManager";
import type { AmountDto } from "Infrastructure/Api/Api";
import type { CurrencyCode } from "Models/CurrencyCodes";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { formatCurrency, getCurrencySymbol } from "Utils/CurrencyUtils";

const RESOURCES = Resources.Components.Transactions.Summary;

const getTitle = (
  { payPoint, employee, status, timePeriod }: TransactionFilters,
  t: TFunction,
) => {
  const hasOtherFilters = payPoint || employee || status;
  const suffix = hasOtherFilters ? ` ${t(RESOURCES.FilterSuffix)}` : "";

  if (!timePeriod) {
    return t(RESOURCES.UnlimitedRevenue) + suffix;
  }

  return t(RESOURCES.Revenue[timePeriod]) + suffix;
};

type Props = {
  filters: TransactionFilters;
  totalSums: Array<AmountDto>;
  isLoading?: boolean;
  emptyStateCurrency?: CurrencyCode;
};

export const TransactionsSummaryBox = ({
  filters,
  totalSums,
  isLoading,
  emptyStateCurrency,
}: Props) => {
  const { t } = useResource();

  const title = getTitle(filters, t);

  // The API returns one entry per currency; sums must never be combined across
  // currencies, so each entry renders as its own row.
  const displaySums: Array<{ value: number; currencyCode?: string }> =
    totalSums.length > 0
      ? totalSums
      : [{ value: 0, currencyCode: emptyStateCurrency }];

  return (
    <Paper sx={{ borderRadius: 2.5 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mt={[4, 6]}
        ml={[4, 6]}
        mr={[3, 4]}
        mb={[3, 4]}
      >
        <Stack gap={1}>
          <Typography
            fontSize={16}
            fontWeight={600}
            letterSpacing={0.5}
            color={Colors.Text}
            textTransform="uppercase"
          >
            {title}
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width={230} height={48} />
          ) : (
            displaySums.map(({ value, currencyCode }) => (
              <Box
                key={currencyCode ?? "unknown"}
                display="flex"
                alignItems="baseline"
                gap={1}
              >
                <Typography variant="h2" fontWeight={500} color="primary">
                  {formatCurrency({
                    amount: value,
                    currencyCode,
                    minDecimalPlaces: 2,
                    isNumberOnly: true,
                  })}
                </Typography>
                <Typography variant="h5" fontWeight={400} color="primary">
                  {currencyCode ? getCurrencySymbol(currencyCode) : ""}
                </Typography>
              </Box>
            ))
          )}
        </Stack>
        <ExportActions filters={filters} />
      </Box>
    </Paper>
  );
};
